import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Globe } from "lucide-react";

const LanguageSwitcher = ({ variant = 'header' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const dropdownRef = useRef(null);

  const flags = {
    en: "https://flagcdn.com/w40/gb.png",
    de: "https://flagcdn.com/w40/de.png",
    ru: "https://flagcdn.com/w40/ru.png",
    ar: "https://flagcdn.com/w40/sa.png",
  };

  const languages = [
    { code: "en", label: "English" },
    { code: "de", label: "Deutsch" },
    { code: "ru", label: "Русский" },
    { code: "ar", label: "العربية" },
  ];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    setShowSheet(false);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {variant === 'mobile-compact' ? (
          /* Mobile header: compact round flag button */
          <button
            onClick={() => setShowSheet(true)}
            className="w-10 h-10 flex items-center justify-center bg-white/15 rounded-full text-white hover:bg-white/25 transition-all active:scale-95"
          >
            <img
              src={flags[i18n.language] || flags.en}
              alt={i18n.language}
              className="w-5 h-5 rounded-full object-cover border border-white/30"
            />
          </button>
        ) : (
          /* Desktop / sidebar: full button with label */
          <button
            onClick={() => {
              if (window.innerWidth < 768) {
                setShowSheet(true);
              } else {
                setIsOpen(!isOpen);
              }
            }}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 font-bold text-sm outline-none ${
              variant === 'sidebar'
                ? 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-white/10'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <img
              src={flags[i18n.language] || flags.en}
              alt={i18n.language}
              className="w-5 h-5 rounded-full object-cover border border-white/20"
            />
            <span className="uppercase">{i18n.language}</span>
            <ChevronDown size={14} className={`hidden md:block transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
          </button>
        )}

        {/* Desktop dropdown */}
        {isOpen && (
          <div className="hidden md:block absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl py-2 z-[1000] border border-slate-100 animate-in fade-in zoom-in-95 duration-200">
            <div className="px-3 py-1 text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
              {t("Select Language")}
            </div>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`
                  w-full flex items-center justify-between px-4 py-2.5 text-sm font-bold transition-all
                  ${i18n.language === lang.code
                    ? "text-[#0f7699] bg-[#0f7699]/5"
                    : "text-slate-600 hover:bg-slate-50 hover:text-[#0f7699]"}
                `}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={flags[lang.code]}
                    alt={lang.code}
                    className="w-5 h-5 rounded-full object-cover border border-slate-200 shadow-sm"
                  />
                  <span>{lang.label}</span>
                </div>
                {i18n.language === lang.code && <Check size={14} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      {showSheet && (
        <div className="fixed inset-0 z-[2600] md:hidden" onClick={() => setShowSheet(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200" />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl animate-in slide-in-from-bottom duration-300" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-slate-300 dark:bg-slate-700 rounded-full" />
            </div>
            <div className="px-5 pb-3 pt-1">
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{t("Select Language")}</h3>
            </div>
            <div className="px-3 pb-8">
              {languages.map((lang) => {
                const isSelected = i18n.language === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl mb-1 transition-all active:scale-[0.98] ${
                      isSelected
                        ? "bg-[#0f7699]/10 dark:bg-[#4fc3f7]/15 text-[#0f7699] dark:text-[#4fc3f7]"
                        : "text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    <img
                      src={flags[lang.code]}
                      alt={lang.code}
                      className="w-8 h-8 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700 shadow-sm"
                    />
                    <span className="text-sm font-bold flex-1 text-left">{lang.label}</span>
                    {isSelected && <Check size={18} className="text-[#0f7699] dark:text-[#4fc3f7]" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LanguageSwitcher;
